import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  EyeOff,
  Briefcase,
  ShoppingCart,
  Server,
  MessageSquare,
  Bot,
  Zap,
  Star,
  TrendingUp,
  Shield,
  Target,
  CheckCircle,
  Award,
  Clock,
  Settings,
  Play,
  Search,
  Heart,
  ThumbsUp,
  Smile,
  Gift,
  Lightbulb,
  Rocket,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Users
} from 'lucide-react';
import { createSlug } from '../../../lib/utils';
import { API_URLS } from '../../../config/api';
import { adminApiFetch } from '../../../utils/adminApi';

const AdminServices = () => {
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [serviceStats, setServiceStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [serviceProcess, setServiceProcess] = useState([]);
  const [processLoading, setProcessLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    detailed_description: '',
    icon: 'Briefcase',
    color: 'from-blue-500 to-blue-700',
    bg_color: 'bg-blue-50',
    features: [''],
    price: '',
    badge: '',
    is_active: true,
    sort_order: 0
  });

  // Icon mapping
  const iconMap = {
    'Briefcase': Briefcase,
    'ShoppingCart': ShoppingCart,
    'Server': Server,
    'MessageSquare': MessageSquare,
    'Bot': Bot,
    'Zap': Zap,
    'Star': Star,
    'TrendingUp': TrendingUp,
    'Shield': Shield,
    'Target': Target,
    'CheckCircle': CheckCircle,
    'Award': Award,
    'Clock': Clock,
    'Settings': Settings,
    'Play': Play,
    'Search': Search,
    'Heart': Heart,
    'ThumbsUp': ThumbsUp,
    'Smile': Smile,
    'Gift': Gift,
    'Lightbulb': Lightbulb,
    'Rocket': Rocket,
    'ArrowRight': ArrowRight,
    'ArrowLeft': ArrowLeft,
    'ArrowUp': ArrowUp,
    'ArrowDown': ArrowDown,
    'Users': Users
  };

  const iconOptions = [
    { value: 'Briefcase', label: 'Briefcase' },
    { value: 'ShoppingCart', label: 'Shopping Cart' },
    { value: 'Server', label: 'Server' },
    { value: 'MessageSquare', label: 'Message Square' },
    { value: 'Bot', label: 'Bot' },
    { value: 'Zap', label: 'Zap' },
    { value: 'Star', label: 'Star' },
    { value: 'TrendingUp', label: 'Trending Up' },
    { value: 'Shield', label: 'Shield' }
  ];

  const colorOptions = [
    { value: 'from-blue-400 to-blue-600', label: 'Mavi (Açık)' },
    { value: 'from-green-400 to-green-600', label: 'Yeşil (Açık)' },
    { value: 'from-orange-400 to-orange-600', label: 'Turuncu (Açık)' },
    { value: 'from-purple-400 to-purple-600', label: 'Mor (Açık)' },
    { value: 'from-pink-400 to-pink-600', label: 'Pembe (Açık)' },
    { value: 'from-red-400 to-red-600', label: 'Kırmızı (Açık)' },
    { value: 'from-indigo-400 to-indigo-600', label: 'İndigo (Açık)' },
    { value: 'from-yellow-400 to-yellow-600', label: 'Sarı (Açık)' },
    { value: 'from-blue-500 to-blue-700', label: 'Mavi (Orta)' },
    { value: 'from-green-500 to-green-700', label: 'Yeşil (Orta)' },
    { value: 'from-orange-500 to-orange-700', label: 'Turuncu (Orta)' },
    { value: 'from-purple-500 to-purple-700', label: 'Mor (Orta)' },
    { value: 'from-pink-500 to-pink-700', label: 'Pembe (Orta)' },
    { value: 'from-red-500 to-red-700', label: 'Kırmızı (Orta)' },
    { value: 'from-indigo-500 to-indigo-700', label: 'İndigo (Orta)' },
    { value: 'from-yellow-500 to-yellow-700', label: 'Sarı (Orta)' }
  ];

  const bgColorOptions = [
    { value: 'bg-blue-50', label: 'Mavi Açık' },
    { value: 'bg-green-50', label: 'Yeşil Açık' },
    { value: 'bg-orange-50', label: 'Turuncu Açık' },
    { value: 'bg-purple-50', label: 'Mor Açık' },
    { value: 'bg-pink-50', label: 'Pembe Açık' },
    { value: 'bg-red-50', label: 'Kırmızı Açık' },
    { value: 'bg-indigo-50', label: 'İndigo Açık' },
    { value: 'bg-yellow-50', label: 'Sarı Açık' }
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await adminApiFetch(API_URLS.ADMIN.SERVICES);
      const data = await response.json();
      if (data.status === 'success') {
        setServices(data.data);
      } else {
        console.error('Error fetching services:', data.message);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Form validasyonu
    if (!formData.title || !formData.title.trim()) {
      alert('Lütfen hizmet başlığı girin');
      return;
    }

    try {
      const serviceData = {
        title: formData.title.trim(),
        slug: formData.slug || createSlug(formData.title),
        description: formData.description || '',
        detailed_description: formData.detailed_description || '',
        icon: formData.icon || 'Briefcase',
        color: formData.color || 'from-blue-500 to-blue-700',
        bg_color: formData.bg_color || 'bg-blue-50',
        features: JSON.stringify(formData.features ? formData.features.filter(f => f && f.trim() !== '') : []),
        price: formData.price || '',
        badge: formData.badge || '',
        is_active: formData.is_active !== false,
        sort_order: formData.sort_order || 0
      };

      let response;
      if (editingService) {
        response = await adminApiFetch(`${API_URLS.ADMIN.UPDATE_SERVICE}&id=${editingService.id}`, {
          method: 'PUT',
          body: JSON.stringify(serviceData)
        });
      } else {
        response = await adminApiFetch(API_URLS.ADMIN.CREATE_SERVICE, {
          method: 'POST',
          body: JSON.stringify(serviceData)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        alert(editingService ? 'Hizmet başarıyla güncellendi!' : 'Hizmet başarıyla eklendi!');
        await fetchServices();
        resetForm();
      } else {
        alert('Hata: ' + (result.message || 'Hizmet kaydedilemedi'));
      }
    } catch (error) {
      console.error('Error saving service:', error);
      const errorMessage = error.message || 'Bilinmeyen hata';
      alert('Hizmet kaydedilirken bir hata oluştu: ' + errorMessage);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title || '',
              slug: service.slug || createSlug(service.title || service.name || '') || '',
      description: service.description || '',
      detailed_description: service.detailed_description || '',
      icon: service.icon || 'Briefcase',
      color: service.color || 'from-blue-400 to-blue-600',
      bg_color: service.bg_color || 'bg-blue-50',
      features: typeof service.features === 'string' ? JSON.parse(service.features || '[]') : (service.features || ['']),
      price: service.price || '',
      badge: service.badge || '',
      is_active: service.is_active || true,
      sort_order: service.sort_order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm('Bu hizmeti silmek istediğinizden emin misiniz?')) {
      try {
        await adminApiFetch(`${API_URLS.ADMIN.DELETE_SERVICE}&id=${serviceId}`, {
          method: 'DELETE'
        });
        fetchServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      description: '',
      detailed_description: '',
      icon: 'Briefcase',
      color: 'from-blue-400 to-blue-600',
      bg_color: 'bg-blue-50',
      features: [''],
      price: '',
      badge: '',
      is_active: true,
      sort_order: 0
    });
    setEditingService(null);
    setShowForm(false);
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => i === index ? value : feature)
    }));
  };

  // Stats management functions
  const handleManageStats = async (service) => {
    setSelectedService(service);
    setShowStatsModal(true);
    setStatsLoading(true);

    try {
              const response = await adminApiFetch(`${API_URLS.ADMIN.GET_SERVICE_STATS}&service_id=${service.id}`);
      const responseData = await response.json();
      // Admin API response formatını kontrol et
      const statsData = responseData.status === 'success' ? responseData.data : [];
      setServiceStats(statsData);
    } catch (error) {
      console.error('Error fetching service stats:', error);
      setServiceStats([]);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleAddStat = async (statData) => {
    try {
              const response = await adminApiFetch(`${API_URLS.ADMIN.UPDATE_SERVICE_STATS}&service_id=${selectedService.id}`, {
                method: 'POST',
                body: JSON.stringify(statData)
              });
      const responseData = await response.json();
      if (responseData.status === 'success' && responseData.data) {
        setServiceStats(prev => [...prev, responseData.data]);
      }
    } catch (error) {
      console.error('Error adding stat:', error);
      alert('Stat eklenirken hata oluştu');
    }
  };

  const handleUpdateStat = async (statId, statData) => {
    try {
              const response = await adminApiFetch(`${API_URLS.ADMIN.UPDATE_SERVICE_STAT}&stat_id=${statId}`, {
                method: 'PUT',
                body: JSON.stringify(statData)
              });
      const responseData = await response.json();
      // API response formatını kontrol et ve güncellenmiş veriyi al
      const updatedStat = responseData.status === 'success' ? responseData.data : responseData;
      setServiceStats(prev => prev.map(stat => stat.id === statId ? updatedStat : stat));
    } catch (error) {
      console.error('Error updating stat:', error);
      alert('Stat güncellenirken hata oluştu');
    }
  };

  const handleDeleteStat = async (statId) => {
    if (!confirm('Bu stat\'ı silmek istediğinizden emin misiniz?')) return;

    try {
              await adminApiFetch(`${API_URLS.ADMIN.DELETE_SERVICE_STAT}&stat_id=${statId}`, {
                method: 'DELETE'
              });
      setServiceStats(prev => prev.filter(stat => stat.id !== statId));
    } catch (error) {
      console.error('Error deleting stat:', error);
      alert('Stat silinirken hata oluştu');
    }
  };

  // Process management functions
  const handleManageProcess = async (service) => {
    setSelectedService(service);
    setShowProcessModal(true);
    setProcessLoading(true);

    try {
              const response = await adminApiFetch(`${API_URLS.ADMIN.GET_SERVICE_PROCESS}&service_id=${service.id}`);
      const responseData = await response.json();
      // Admin API response formatını kontrol et
      const processData = responseData.status === 'success' ? responseData.data : [];
      setServiceProcess(processData);
    } catch (error) {
      console.error('Error fetching service process:', error);
      setServiceProcess([]);
    } finally {
      setProcessLoading(false);
    }
  };

  const handleAddProcessStep = async (processData) => {
    try {
              const response = await adminApiFetch(`${API_URLS.ADMIN.UPDATE_SERVICE_PROCESS}&service_id=${selectedService.id}`, {
                method: 'POST',
                body: JSON.stringify(processData)
              });
      const responseData = await response.json();
      if (responseData.status === 'success' && responseData.data) {
        setServiceProcess(prev => [...prev, responseData.data]);
      }
    } catch (error) {
      console.error('Error adding process step:', error);
      alert('Process adımı eklenirken hata oluştu');
    }
  };

  const handleUpdateProcessStep = async (processId, processData) => {
    try {
              const response = await adminApiFetch(`${API_URLS.ADMIN.UPDATE_SERVICE_PROCESS_STEP}&process_id=${processId}`, {
                method: 'PUT',
                body: JSON.stringify(processData)
              });
      const responseData = await response.json();
      // API response formatını kontrol et ve güncellenmiş veriyi al
      const updatedStep = responseData.status === 'success' ? responseData.data : responseData;
      setServiceProcess(prev => prev.map(step => step.id === processId ? updatedStep : step));
    } catch (error) {
      console.error('Error updating process step:', error);
      alert('Process adımı güncellenirken hata oluştu');
    }
  };

  const handleDeleteProcessStep = async (processId) => {
    if (!confirm('Bu process adımını silmek istediğinizden emin misiniz?')) return;

    try {
              await adminApiFetch(`${API_URLS.ADMIN.DELETE_SERVICE_PROCESS_STEP}&process_id=${processId}`, {
                method: 'DELETE'
              });
      setServiceProcess(prev => prev.filter(step => step.id !== processId));
    } catch (error) {
      console.error('Error deleting process step:', error);
      alert('Process adımı silinirken hata oluştu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Hizmet Yönetimi</h1>
          <p className="text-gray-600">Anasayfadaki hizmetleri yönetin</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Hizmet
        </Button>
      </div>

      {/* Services List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="bg-gray-100 p-6 rounded-t-lg">
                <div className="h-12 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="h-8 bg-gray-200 rounded"></div>
                  <div className="space-y-2">
                    {[1, 2, 3].map((j) => (
                      <div key={`${i}-${j}`} className="h-4 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : services && services.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || Briefcase;
            return (
              <Card key={service.id} className="relative">
                <CardHeader className={`${service.bg_color} pb-4`}>
                  <div className="flex items-center justify-between">
                    <div className={`p-2 rounded-lg bg-gradient-to-r ${service.color} text-white`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex items-center space-x-2">
                      {service.badge && (
                        <Badge variant="secondary" className="text-xs">
                          {service.badge}
                        </Badge>
                      )}
                      <Badge variant={service.is_active ? "default" : "secondary"} className="text-xs">
                        {service.is_active ? 'Aktif' : 'Pasif'}
                      </Badge>
                    </div>
                  </div>
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    {service.title}
                  </CardTitle>
                  <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">
                    {service.description}
                  </p>
                </CardHeader>

                <CardContent className="space-y-4 p-4 sm:p-6">
                  {(() => {
                    const features = typeof service.features === 'string' ? JSON.parse(service.features || '[]') : (service.features || []);
                    return features && features.length > 0 && (
                      <div className="space-y-2">
                        {features.slice(0, 3).map((feature, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0"></div>
                            <span className="text-xs text-gray-600 truncate">{feature}</span>
                          </div>
                        ))}
                        {features.length > 3 && (
                          <span className="text-xs text-gray-500">+{features.length - 3} daha</span>
                        )}
                      </div>
                    );
                  })()}

                  <div className="flex items-center justify-between">
                    <span className="text-base sm:text-lg font-bold text-gray-900 truncate">{service.price}</span>
                    <span className="text-xs text-gray-500 flex-shrink-0 ml-2">Sıra: {service.sort_order}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(service)}
                      className="flex-1 min-w-[80px] sm:min-w-0"
                    >
                      <Edit className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Düzenle</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageStats(service)}
                      className="flex-1 min-w-[80px] sm:min-w-0"
                    >
                      <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Stats</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManageProcess(service)}
                      className="flex-1 min-w-[80px] sm:min-w-0"
                    >
                      <Target className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                      <span className="hidden sm:inline">Process</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(service.id)}
                      className="text-red-600 hover:text-red-700 flex-shrink-0"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">Henüz hizmet bulunmuyor.</p>
          <Button onClick={() => fetchServices()} variant="outline">
            Tekrar Dene
          </Button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet Ekle'}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title">Başlık</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="slug">
                      URL Slug
                      <span className="text-red-500 ml-1">*</span>
                    </Label>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="ornek-hizmet-adi"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      URL: <span className="font-mono">/hizmet/{formData.slug || createSlug(formData.title || '')}</span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Fiyat</Label>
                    <Input
                      id="price"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="₺299/ay"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">
                    Hizmet Açıklaması
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    placeholder="Bu açıklama tekil hizmet sayfasında başlık altında görünecektir. Örnek: Projeleriniz için en uygun freelancerları bulun. Güvenli teklif ve seçim süreci."
                    required
                    className="resize-none"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Bu metin tekil hizmet sayfasında başlık altında görüntülenir
                    </p>
                    <p className="text-xs text-gray-400">
                      {formData.description.length}/200 karakter
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="detailed_description">
                    Detaylı Hizmet Açıklaması
                    <span className="text-gray-500 ml-1">(Opsiyonel)</span>
                  </Label>
                  <Textarea
                    id="detailed_description"
                    value={formData.detailed_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, detailed_description: e.target.value }))}
                    rows={6}
                    placeholder="Bu açıklama tekil hizmet sayfasında 'Hizmet Detayları' bölümünde görünecektir. Hizmetin detayları, özellikleri ve avantajları hakkında kapsamlı bilgi verin."
                    className="resize-none"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Bu metin tekil hizmet sayfasında 'Hizmet Detayları' bölümünde görüntülenir
                    </p>
                    <p className="text-xs text-gray-400">
                      {formData.detailed_description.length}/1000 karakter
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="icon">İkon</Label>
                    <select
                      id="icon"
                      value={formData.icon}
                      onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      {iconOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="color">Renk</Label>
                    <select
                      id="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      {colorOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="bg_color">Arka Plan</Label>
                    <select
                      id="bg_color"
                      value={formData.bg_color}
                      onChange={(e) => setFormData(prev => ({ ...prev, bg_color: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      {bgColorOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="badge">Rozet (Opsiyonel)</Label>
                    <Input
                      id="badge"
                      value={formData.badge}
                      onChange={(e) => setFormData(prev => ({ ...prev, badge: e.target.value }))}
                      placeholder="Popüler, Yeni, vs."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sort_order">Sıralama</Label>
                    <Input
                      id="sort_order"
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                      min="0"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      id="is_active"
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="is_active">Aktif</Label>
                  </div>
                </div>

                <div>
                  <Label>Özellikler</Label>
                  <div className="space-y-2">
                    {formData.features && formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Input
                          value={feature}
                          onChange={(e) => updateFeature(index, e.target.value)}
                          placeholder="Özellik açıklaması"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeFeature(index)}
                          className="text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addFeature}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Özellik Ekle
                    </Button>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                    <Save className="w-4 h-4 mr-2" />
                    {editingService ? 'Güncelle' : 'Kaydet'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    İptal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Process Management Modal */}
      {showProcessModal && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {selectedService.title} - Çalışma Süreci Yönetimi
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowProcessModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              {processLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Add New Process Step Form */}
                  <div className="border rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-4">Yeni Süreç Adımı Ekle</h3>
                    <AddProcessForm onAdd={handleAddProcessStep} />
                  </div>

                  {/* Existing Process Steps */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Mevcut Süreç Adımları</h3>
                    {!serviceProcess || serviceProcess.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Henüz süreç adımı eklenmemiş</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Array.isArray(serviceProcess) && serviceProcess.map((step, index) => (
                          <ProcessCard
                            key={step.id || `step-${index}`}
                            step={step}
                            onUpdate={handleUpdateProcessStep}
                            onDelete={handleDeleteProcessStep}
                            iconMap={iconMap}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Management Modal */}
      {showStatsModal && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {selectedService.title} - İstatistik Yönetimi
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowStatsModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent>
              {statsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Add New Stat Form */}
                  <div className="border rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-4">Yeni İstatistik Ekle</h3>
                    <AddStatForm onAdd={handleAddStat} />
                  </div>

                  {/* Existing Stats */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Mevcut İstatistikler</h3>
                    {!serviceStats || serviceStats.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Henüz istatistik eklenmemiş</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Array.isArray(serviceStats) && serviceStats.map((stat, index) => (
                          <StatCard
                            key={stat.id || `stat-${index}`}
                            stat={stat}
                            onUpdate={handleUpdateStat}
                            onDelete={handleDeleteStat}
                            iconMap={iconMap}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Add Stat Form Component
const AddStatForm = ({ onAdd }) => {
  const [formData, setFormData] = useState({
    title: '',
    value: '',
    icon: 'Briefcase',
    sort_order: 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.value) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }
    onAdd(formData);
    setFormData({ title: '', value: '', icon: 'Briefcase', sort_order: 0 });
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <Label>Başlık</Label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Örn: Tamamlanan Proje"
        />
      </div>
      <div>
        <Label>Değer</Label>
        <Input
          value={formData.value}
          onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
          placeholder="Örn: 150+"
        />
      </div>
      <div>
        <Label>İkon</Label>
        <select
          value={formData.icon}
          onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
          className="w-full p-2 border border-gray-300 rounded-md"
        >
          <option key="Briefcase" value="Briefcase">Briefcase</option>
          <option key="Users" value="Users">Users</option>
          <option key="Star" value="Star">Star</option>
          <option key="TrendingUp" value="TrendingUp">TrendingUp</option>
          <option key="Shield" value="Shield">Shield</option>
          <option key="Zap" value="Zap">Zap</option>
          <option key="CheckCircle" value="CheckCircle">CheckCircle</option>
          <option key="Award" value="Award">Award</option>
          <option key="Clock" value="Clock">Clock</option>
          <option key="Target" value="Target">Target</option>
          <option key="Settings" value="Settings">Settings</option>
          <option key="Play" value="Play">Play</option>
          <option key="Search" value="Search">Search</option>
          <option key="MessageSquare" value="MessageSquare">MessageSquare</option>
          <option key="Heart" value="Heart">Heart</option>
          <option key="ThumbsUp" value="ThumbsUp">ThumbsUp</option>
          <option key="Smile" value="Smile">Smile</option>
          <option key="Gift" value="Gift">Gift</option>
          <option key="Lightbulb" value="Lightbulb">Lightbulb</option>
          <option key="Rocket" value="Rocket">Rocket</option>
        </select>
      </div>
      <div className="flex items-end">
        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Ekle
        </Button>
      </div>
    </form>
  );
};

// Stat Card Component
const StatCard = ({ stat, onUpdate, onDelete, iconMap }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: stat.title || '',
    value: stat.value || '',
    icon: stat.icon || 'Briefcase',
    sort_order: stat.sort_order || 0
  });

  const handleSave = () => {
    onUpdate(stat.id, editData);
    setIsEditing(false);
  };

  const IconComponent = iconMap[stat.icon] || Briefcase;

  return (
    <Card>
      <CardContent className="p-4">
        {isEditing ? (
          <div className="space-y-3">
            <Input
              value={editData.title}
              onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Başlık"
            />
            <Input
              value={editData.value}
              onChange={(e) => setEditData(prev => ({ ...prev, value: e.target.value }))}
              placeholder="Değer"
            />
            <select
              value={editData.icon}
              onChange={(e) => setEditData(prev => ({ ...prev, icon: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option key="Briefcase" value="Briefcase">Briefcase</option>
              <option key="Users" value="Users">Users</option>
              <option key="Star" value="Star">Star</option>
              <option key="TrendingUp" value="TrendingUp">TrendingUp</option>
              <option key="Shield" value="Shield">Shield</option>
              <option key="Zap" value="Zap">Zap</option>
              <option key="CheckCircle" value="CheckCircle">CheckCircle</option>
              <option key="Award" value="Award">Award</option>
            </select>
            <div className="flex space-x-2">
              <Button size="sm" onClick={handleSave}>
                <Save className="w-4 h-4 mr-1" />
                Kaydet
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-1" />
                İptal
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <IconComponent className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold">{stat.title}</p>
                <p className="text-lg font-bold text-purple-600">{stat.value}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDelete(stat.id)} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Add Process Form Component
const AddProcessForm = ({ onAdd }) => {
  const [formData, setFormData] = useState({
    step_order: 1,
    step_title: '',
    step_description: '',
    icon: 'MessageSquare'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.step_title || !formData.step_description) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }
    onAdd(formData);
    setFormData({ step_order: 1, step_title: '', step_description: '', icon: 'MessageSquare' });
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-4">
      <div>
        <Label>Adım No</Label>
        <Input
          type="number"
          value={formData.step_order}
          onChange={(e) => setFormData(prev => ({ ...prev, step_order: parseInt(e.target.value) || 1 }))}
          min="1"
        />
      </div>
      <div>
        <Label>Başlık</Label>
        <Input
          value={formData.step_title}
          onChange={(e) => setFormData(prev => ({ ...prev, step_title: e.target.value }))}
          placeholder="Örn: Görüşme"
        />
      </div>
      <div>
        <Label>Açıklama</Label>
        <Input
          value={formData.step_description}
          onChange={(e) => setFormData(prev => ({ ...prev, step_description: e.target.value }))}
          placeholder="Örn: Lorem ipsum dolor sit amet."
        />
      </div>
      <div>
        <Label>İkon</Label>
        <select
          value={formData.icon}
          onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
          className="w-full p-2 border border-gray-300 rounded-md"
        >
          <option key="MessageSquare" value="MessageSquare">MessageSquare</option>
          <option key="Target" value="Target">Target</option>
          <option key="CheckCircle" value="CheckCircle">CheckCircle</option>
          <option key="Briefcase" value="Briefcase">Briefcase</option>
          <option key="Users" value="Users">Users</option>
          <option key="Star" value="Star">Star</option>
          <option key="TrendingUp" value="TrendingUp">TrendingUp</option>
          <option key="Shield" value="Shield">Shield</option>
          <option key="Clock" value="Clock">Clock</option>
          <option key="Settings" value="Settings">Settings</option>
          <option key="Play" value="Play">Play</option>
          <option key="Search" value="Search">Search</option>
          <option key="Zap" value="Zap">Zap</option>
          <option key="Award" value="Award">Award</option>
          <option key="Heart" value="Heart">Heart</option>
          <option key="ThumbsUp" value="ThumbsUp">ThumbsUp</option>
          <option key="Smile" value="Smile">Smile</option>
          <option key="Gift" value="Gift">Gift</option>
          <option key="Lightbulb" value="Lightbulb">Lightbulb</option>
          <option key="Rocket" value="Rocket">Rocket</option>
          <option key="ArrowRight" value="ArrowRight">ArrowRight</option>
          <option key="ArrowLeft" value="ArrowLeft">ArrowLeft</option>
          <option key="ArrowUp" value="ArrowUp">ArrowUp</option>
          <option key="ArrowDown" value="ArrowDown">ArrowDown</option>
        </select>
      </div>

      <div className="flex items-end">
        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Ekle
        </Button>
      </div>
    </form>
  );
};

// Process Card Component
const ProcessCard = ({ step, onUpdate, onDelete, iconMap }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    step_order: step.step_order || 1,
    step_title: step.step_title || '',
    step_description: step.step_description || '',
    icon: step.icon || 'MessageSquare'
  });

  const handleSave = () => {
    onUpdate(step.id, editData);
    setIsEditing(false);
  };

  const IconComponent = iconMap[step.icon] || MessageSquare;

  return (
    <Card>
      <CardContent className="p-4">
        {isEditing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                value={editData.step_order}
                onChange={(e) => setEditData(prev => ({ ...prev, step_order: parseInt(e.target.value) || 1 }))}
                placeholder="Adım No"
                min="1"
              />
              <select
                value={editData.icon}
                onChange={(e) => setEditData(prev => ({ ...prev, icon: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option key="MessageSquare" value="MessageSquare">MessageSquare</option>
                <option key="Target" value="Target">Target</option>
                <option key="CheckCircle" value="CheckCircle">CheckCircle</option>
                <option key="Briefcase" value="Briefcase">Briefcase</option>
                <option key="Users" value="Users">Users</option>
                <option key="Star" value="Star">Star</option>
                <option key="TrendingUp" value="TrendingUp">TrendingUp</option>
                <option key="Shield" value="Shield">Shield</option>
                <option key="Clock" value="Clock">Clock</option>
                <option key="Settings" value="Settings">Settings</option>
                <option key="Play" value="Play">Play</option>
                <option key="Search" value="Search">Search</option>
                <option key="Zap" value="Zap">Zap</option>
                <option key="Award" value="Award">Award</option>
                <option key="Heart" value="Heart">Heart</option>
                <option key="ThumbsUp" value="ThumbsUp">ThumbsUp</option>
                <option key="Smile" value="Smile">Smile</option>
                <option key="Gift" value="Gift">Gift</option>
                <option key="Lightbulb" value="Lightbulb">Lightbulb</option>
                <option key="Rocket" value="Rocket">Rocket</option>
                <option key="ArrowRight" value="ArrowRight">ArrowRight</option>
                <option key="ArrowLeft" value="ArrowLeft">ArrowLeft</option>
                <option key="ArrowUp" value="ArrowUp">ArrowUp</option>
                <option key="ArrowDown" value="ArrowDown">ArrowDown</option>
              </select>
            </div>
            <Input
              value={editData.step_title}
              onChange={(e) => setEditData(prev => ({ ...prev, step_title: e.target.value }))}
              placeholder="Başlık"
            />
            <Input
              value={editData.step_description}
              onChange={(e) => setEditData(prev => ({ ...prev, step_description: e.target.value }))}
              placeholder="Açıklama"
            />
            <div className="flex space-x-2">
              <Button size="sm" onClick={handleSave}>
                <Save className="w-4 h-4 mr-1" />
                Kaydet
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-1" />
                İptal
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <IconComponent className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-500">Adım {step.step_order}</span>
                </div>
                <p className="font-semibold">{step.step_title}</p>
                <p className="text-sm text-gray-600">{step.step_description}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDelete(step.id)} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminServices;
